import {useQuery, useQueryClient, keepPreviousData} from '@tanstack/react-query';
import {useEffect} from 'react';

import {matrixService} from 'api/matrix';
import {firebaseService} from 'services/Firebase/Firebase';
import {QUERY_CACHE_KEYS} from 'shared/constants/queryCache';
import {Unread} from 'shared/models/task/chat';

const REFETCH_INTERVAL = 30 * 1000;
const STALE_TIME = 30 * 1000;

const DEFAULT_UNREAD: Unread = {
  total: 0,
  highlight: 0,
  from: new Set(),
};

export const useUnreadMessageCount = (): Unread => {
  const queryClient = useQueryClient();
  const userId = firebaseService.getWorkerIdFromLS();

  const {data: roomIds = []} = useQuery({
    queryKey: QUERY_CACHE_KEYS.joinedRooms(userId),
    queryFn: async () => {
      const rooms = await matrixService.getJoinedRooms();
      return rooms || [];
    },
    enabled: !!userId,
    initialData: [],
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    staleTime: STALE_TIME,
  });

  const {data: unreadInfo = DEFAULT_UNREAD} = useQuery({
    queryKey: QUERY_CACHE_KEYS.unreadCount(userId, roomIds),
    queryFn: async () => {
      if (!roomIds?.length) return DEFAULT_UNREAD;
      const result = await matrixService.getUnreadCount(roomIds);
      return result || DEFAULT_UNREAD;
    },
    enabled: !!userId && !!roomIds && roomIds.length > 0,
    placeholderData: keepPreviousData,
    initialData: DEFAULT_UNREAD,
    refetchInterval: REFETCH_INTERVAL,
    refetchOnMount: true,
    staleTime: STALE_TIME,
  });

  useEffect(() => {
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_CACHE_KEYS.joinedRooms(userId),
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_CACHE_KEYS.unreadCount(userId, roomIds),
      });
    }
  }, [userId, queryClient, roomIds]);

  return unreadInfo;
};
